import { SeoProps } from '@common/components/SEO/seo.type';
import useAppPlatform from '@common/hooks/useAppPlatform';
import { isDomainZone } from '@common/utils/lang';
import React from 'react';
import { Helmet } from 'react-helmet-async';

const isAbsUrl = (u?: string) => !!u && /^https?:\/\//i.test(u);

function normalizeHostname(hostname: string): string {
  return hostname.replace(/^m\./i, '');
}

function currentOrigin(): string | undefined {
  if (typeof window !== "undefined" && window.location) {
    const { protocol, hostname, port } = window.location;
    const normalizedHostname = normalizeHostname(hostname);
    const normalizedPort = port ? `:${port}` : '';
    return `${protocol}//${normalizedHostname}${normalizedPort}`;
  }
  return undefined;
}

function currentPathname(): string | undefined {
  if (typeof window !== "undefined" && window.location) {
    return window.location.pathname;
  }
  return undefined;
}

function withCurrentHost(u?: string): string | undefined {
  if (!u) return undefined;
  if (isAbsUrl(u)) {
    const absoluteUrl = new URL(u);
    absoluteUrl.hostname = normalizeHostname(absoluteUrl.hostname);
    return absoluteUrl.toString();
  }
  const origin = currentOrigin();
  if (!origin) return undefined;
  return `${origin}${u.startsWith("/") ? u : `/${u}`}`;
}

function buildRobots({
  robots,
  noindex,
  nofollow,
}: Pick<SeoProps, "robots" | "noindex" | "nofollow">): string | undefined {
  if (robots) return robots;
  return `${noindex ? "noindex" : "index"},${nofollow ? "nofollow" : "follow"}`;
}

export const Seo: React.FC<SeoProps> = ({
  title,
  titleTemplate,
  description,
  canonical,
  noindex,
  nofollow,
  robots,
  og = {},
  alternates = [],
  meta = [],
}) => {
  const { isIos } = useAppPlatform();

  const fallbackPath = currentPathname();
  const rawCanonical = canonical ?? fallbackPath; // может быть относительным
  const computedCanonical = withCurrentHost(rawCanonical);

  const robotsContent = buildRobots({ robots, noindex, nofollow });

  const ogTitle = og.title ?? title ?? undefined;
  const ogDesc = og.description ?? description ?? undefined;
  const ogUrl = og.url ?? computedCanonical ?? undefined;

  const defaultOgImage = currentOrigin() ? `${currentOrigin()}/favicon.png` : undefined;
  const ogImage = og.image ?? defaultOgImage;
  const ogImageAlt = og.imageAlt ?? "Formula 55";

  const ogSiteName =
    og.siteName ?? (isDomainZone("boxbet") ? "Boxbet" : "Formula 55");

  const ogType = og.type ?? "website";

  let finalTitle =
    titleTemplate && title ? titleTemplate.replace("%s", title) : title ?? undefined;
  if (isIos) {
    finalTitle = '';
  }

  return (
    <Helmet>
      {finalTitle && <title>{finalTitle} | Лицензированный букмекер «{isDomainZone('boxbet') ? 'Boxbet' : 'Формула 55'}»</title>}

      {description && <meta name="description" content={`${description} на сайте компании ${isDomainZone('boxbet') ? '«Boxbet» — Делайте ставки онлайн: безопасно, быстро, удобно!' : '«Формула 55» — первый национальный лицензированный букмекер в Таджикистане. ✅ Делайте ставки онлайн: безопасно, быстро, удобно!'}`} />}

      {computedCanonical && <link rel="canonical" href={computedCanonical} />}

      {robotsContent && <meta name="robots" content={robotsContent} />}

      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDesc && <meta property="og:description" content={ogDesc} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {ogSiteName && <meta property="og:site_name" content={ogSiteName} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      {og.locale && <meta property="og:locale" content={og.locale} />}

      {alternates.map(({ href, hrefLang }) => (
        <link
          key={`${hrefLang}-${href}`}
          rel="alternate"
          hrefLang={hrefLang}
          href={href}
        />
      ))}

      {meta.map((m, i) =>
        m.property ? (
          <meta
            key={`p-${i}-${m.property}`}
            property={m.property}
            content={m.content}
          />
        ) : (
          <meta key={`n-${i}-${m.name}`} name={m.name} content={m.content} />
        )
      )}
    </Helmet>
  );
};
